import { IApiBaseProps, IResultResponse } from '@common/api.models';
import { envConfigs } from '@config/envConfigs';
import { IItemResponse, IListResponse, IPagedResultsV1 } from '@gcl/react-core';
import { IProjectTransmittalHistoryItemRevision } from '@transmittal/history/transmittalHistory.models';
import { ETransmittalType } from '@transmittal/transmittal.constants';
import {
  ICreateProjectTransmittalItemRevisionRequest,
  IProjectTransmittalItemRevision,
  IUpsertProjectTransmittalItemRevisionRequest,
} from '@transmittal/transmittalItemRevision/transmittalItemRevision.models';
import { apiServerDelete, apiServerGet, apiServerPost, apiServerPut } from '@common/axios.services';
import {
  ICheckDocNoxRequest,
  IProjectTransmittalItem,
  IProjectTransmittalItemDrawing,
  IUpdateProjectTransmittalItemRequest,
  IUpsertProjectTransmittalItemAndRevisionRequest,
} from './transmittalItem.models';
import { ETransmittalItemDocumentType } from './transmittalItemDocumentType/transmittalItemDocumentType.constants';

export const apiRoutes = {
  getTransmittalItemsWithLatestRevision: '/Transmittals/Items/WithLatestRevision',
  transmittalItemRevisions: (id: number) => `/Transmittals/${id}/ItemRevisions`,
  transmittalItems: '/Transmittals/Items',
  upsertTransmittalItemsAndRevisions: (id: number) => `/Transmittals/${id}/Items/Upsert`,
  transmittalItemsWithId: (transmittalId: number, transmittalItemId: number) =>
    `/Transmittals/${transmittalId}/Items/${transmittalItemId}`,
  transmittalItemsDrawings: '/Transmittals/Items/Drawings',
  transmittalItemsRemoveFromUse: (id: number, transmittalItemId: number) =>
    `/Transmittals/${id}/Items/${transmittalItemId}/RemoveFromUse`,
  transmittalItemsReuse: (id: number, transmittalItemId: number) =>
    `/Transmittals/${id}/Items/${transmittalItemId}/Reuse`,
  transmittalItemsLatestRevisions: `/Transmittals/Items/LatestRevisions`,
  transmittalItemsDocNosCheck: '/Transmittals/Items/DocNos/Check',
};

export interface IUpdateProjectTransmittalItemProps {
  transmittalId: number;
  transmittalItemId: number;
  request: IUpdateProjectTransmittalItemRequest;
}
export const putTransmittalItemAsync = async (
  props: IUpdateProjectTransmittalItemProps,
): Promise<IItemResponse<IProjectTransmittalItem>> => {
  const result = await apiServerPut<IItemResponse<IProjectTransmittalItem>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemsWithId(props.transmittalId, props.transmittalItemId)}`,
    props.request,
  );
  return result.data;
};

export const deleteTransmittalItemAsync = async (
  transmittalId: number,
  transmittalItemId: number,
): Promise<boolean> => {
  const result = await apiServerDelete<boolean>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemsWithId(transmittalId, transmittalItemId)}`,
  );
  return result.data;
};

export interface IGetTransmittalItemsWithLatestRevisionProps extends IApiBaseProps {
  masterScheduleId?: number;
  transmittalType?: ETransmittalType;
  documentTypeId?: ETransmittalItemDocumentType;
}

export const getTransmittalItemsWithLatestRevisionAsync = async (
  props?: IGetTransmittalItemsWithLatestRevisionProps,
): Promise<IPagedResultsV1<IProjectTransmittalItem>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectTransmittalItem>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getTransmittalItemsWithLatestRevision}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const postTransmittalItemRevisionsAsync = async (
  transmittalId: number,
  props: ICreateProjectTransmittalItemRevisionRequest,
): Promise<IItemResponse<IProjectTransmittalItemRevision>> => {
  const result = await apiServerPost<IItemResponse<IProjectTransmittalItemRevision>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemRevisions(transmittalId)}`,
    props,
  );
  return result.data;
};
export const putTransmittalItemRevisionsAsync = async (
  transmittalId: number,
  itemRevisionId: number,
  props: IUpsertProjectTransmittalItemRevisionRequest,
): Promise<IItemResponse<IProjectTransmittalItemRevision>> => {
  const result = await apiServerPut<IItemResponse<IProjectTransmittalItemRevision>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemRevisions(transmittalId)}/${itemRevisionId}`,
    props,
  );
  return result.data;
};

export const upsertTransmittalItemsAndRevisionsAsync = async (
  transmittalId: number,
  props: IUpsertProjectTransmittalItemAndRevisionRequest[],
): Promise<IListResponse<IProjectTransmittalItem>> => {
  const result = await apiServerPost<IListResponse<IProjectTransmittalItem>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.upsertTransmittalItemsAndRevisions(transmittalId)}`,
    props,
  );
  return result.data;
};

export const deleteTransmittalItemRevisionsAsync = async (
  projectId: number,
  itemRevisionId: number,
  transmittalId: number,
): Promise<IItemResponse<IProjectTransmittalItemRevision>> => {
  const result = await apiServerDelete<IItemResponse<IProjectTransmittalItemRevision>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemRevisions(transmittalId)}/${itemRevisionId}`,
    {
      params: {
        projectId,
        itemRevisionId,
      },
    },
  );
  return result.data;
};

export interface IGetTransmittalItemsProps extends IApiBaseProps {
  masterScheduleId?: number;
  transmittalId?: number;
  documentTypeId?: ETransmittalItemDocumentType;
}

export const getTransmittalItemsAsync = async (
  props?: IGetTransmittalItemsProps,
): Promise<IPagedResultsV1<IProjectTransmittalItem>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectTransmittalItem>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItems}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export interface IGetTransmittalItemsDrawingsProps extends IApiBaseProps {
  masterScheduleId: number;
}
export const getTransmittalItemsDrawingsAsync = async (
  props?: IGetTransmittalItemsDrawingsProps,
): Promise<IPagedResultsV1<IProjectTransmittalItemDrawing>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectTransmittalItemDrawing>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemsDrawings}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const postTransmittalItemRemoveFromUseAsync = async (
  transmittalId: number,
  transmittalItemId: number,
  props: IApiBaseProps,
): Promise<IResultResponse> => {
  const result = await apiServerPost<IResultResponse>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemsRemoveFromUse(transmittalId, transmittalItemId)}`,
    null,
    { params: props },
  );
  return result.data;
};

export const postTransmittalItemReuseAsync = async (
  transmittalId: number,
  transmittalItemId: number,
  props: IApiBaseProps,
): Promise<IResultResponse> => {
  const result = await apiServerPost<IResultResponse>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemsReuse(transmittalId, transmittalItemId)}`,
    null,
    { params: props },
  );
  return result.data;
};

export interface IGetTransmittalItemsLatestRevisionProps extends IApiBaseProps {
  projectMasterScheduleId?: number;
  projectTransmittalId?: number;
}

export const getTransmittalItemsLatestRevisionsAsync = async (
  props?: IGetTransmittalItemsLatestRevisionProps,
): Promise<IPagedResultsV1<IProjectTransmittalHistoryItemRevision>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectTransmittalHistoryItemRevision>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemsLatestRevisions}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const postTransmittalItemDocNosCheck = async (data: ICheckDocNoxRequest[]): Promise<boolean> => {
  const result = await apiServerPost<any>(`${envConfigs.apiBaseUrl}${apiRoutes.transmittalItemsDocNosCheck}`, data);
  return result.status === 200;
};
